/**
 * Estado da escuta "Jarvis" (spec 011): o MODO é preferência da pessoa
 * (localStorage espelhado na conta — `prefsNaConta`, desde 26/10/2026; antes
 * era desta máquina); o ESTADO é do momento (ouvindo, pausado…), só em
 * memória, pra o chip da sidebar e a tela de configuração mostrarem.
 *
 * Desligado por padrão. Ligado ('ativo'), "Jarvis" abre o ditado no ao
 * vivo — o modo sombra (só contar) foi pulado a pedido do Leopoldo (25/09).
 *
 * O MOTOR também é desta máquina (fase 5, 30/09/2026): 'chrome' = o
 * reconhecedor local do Chrome (padrão); 'proprio' = Vosk na página, pra
 * máquina onde o do Chrome não devolve texto (o notebook).
 *
 * No celular (spec 024, 23/10/2026) o motor é SEMPRE o próprio: o Chrome do
 * Android não tem o português local e o do iPhone manda o áudio à Apple.
 */

import { useSyncExternalStore } from 'react';
import { CELULAR } from '@/utils/aparelho';

export { CELULAR };

/** Motor e trava de rede: DESTE computador. */
const LS_KEY = 'dratento.voice.wake.v1';
/** Ligada/desligada: segue a conta. Até 26/10/2026 morava no `LS_KEY`. */
const LS_MODO = 'dratento.voice.wake.modo.v1';

export type WakeMode = 'off' | 'ativo';
export type WakeEngine = 'chrome' | 'proprio';

export type WakeState =
  /** Não está escutando (modo desligado, fora da ficha, aba escondida…). */
  | 'parado'
  | 'verificando'
  | 'ouvindo'
  /** O ditado está ouvindo; a escuta da frase se cala. */
  | 'pausado'
  /** O pacote pt-BR local ainda não foi baixado (Configurações → Ditado por voz). */
  | 'instalar'
  /** Navegador sem reconhecimento LOCAL — não escuta, pra nada ir à nuvem. */
  | 'sem-suporte'
  | 'erro';

let estado: WakeState = 'parado';
/**
 * Trava de segurança: o reconhecedor, em modo local, deu erro `network` — ou
 * seja, tentou usar a rede. A escuta fica desligada (inclusive entre
 * recargas) até o médico religar o modo à mão em Configurações.
 */
let bloqueio: string | null = null;
let erro: string | null = null;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

function readPrefs(): { modo: WakeMode; bloqueio: string | null; motor: WakeEngine } {
  try {
    const raw = localStorage.getItem(LS_KEY);
    const p = raw ? (JSON.parse(raw) as { modo?: string; bloqueio?: string; motor?: string }) : {};
    const m = localStorage.getItem(LS_MODO) ?? p.modo;
    // 'sombra' = preferência gravada antes de 25/09; vale como ligado.
    const ligado = m === 'ativo' || m === 'sombra';
    return {
      modo: ligado ? 'ativo' : 'off',
      bloqueio: typeof p.bloqueio === 'string' ? p.bloqueio : null,
      motor: CELULAR || p.motor === 'proprio' ? 'proprio' : 'chrome',
    };
  } catch {
    return { modo: 'off', bloqueio: null, motor: CELULAR ? 'proprio' : 'chrome' };
  }
}

function writePrefs(): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ motor, ...(bloqueio ? { bloqueio } : {}) }));
    localStorage.setItem(LS_MODO, modo);
  } catch {
    /* storage bloqueado: vale só nesta sessão */
  }
}

let modo: WakeMode = 'off';
let motor: WakeEngine = 'chrome';
if (typeof window !== 'undefined') ({ modo, bloqueio, motor } = readPrefs());

// Escuta ligada antes da chave própria: passa pra ela, pra subir pra conta na
// primeira vez deste computador. Desligada não passa — não é escolha, é o
// padrão, e não pode apagar a de outro computador.
if (typeof window !== 'undefined' && modo === 'ativo') {
  try {
    if (localStorage.getItem(LS_MODO) === null) localStorage.setItem(LS_MODO, modo);
  } catch {
    /* storage bloqueado */
  }
}

if (typeof window !== 'undefined') {
  // Outra aba (ou outro computador, pela conta) mudou a preferência.
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_KEY && e.key !== LS_MODO) return;
    ({ modo, bloqueio, motor } = readPrefs());
    emit();
  });
}

export function getWakeMode(): WakeMode {
  return modo;
}

/** Mudar o modo à mão é o que destrava a trava de segurança. */
export function setWakeMode(next: WakeMode): void {
  modo = next;
  bloqueio = null;
  writePrefs();
  emit();
}

export function getWakeEngine(): WakeEngine {
  return motor;
}

/** Trocar o motor também destrava (a trava de rede é coisa do motor do Chrome). */
export function setWakeEngine(next: WakeEngine): void {
  motor = CELULAR ? 'proprio' : next;
  bloqueio = null;
  writePrefs();
  emit();
}

export function getWakeBlock(): string | null {
  return bloqueio;
}

export function blockWake(code: string): void {
  bloqueio = code;
  writePrefs();
  estado = 'erro';
  erro = code;
  emit();
}

export function setWakeState(next: WakeState, detalhe: string | null = null): void {
  if (estado === next && erro === detalhe) return;
  estado = next;
  erro = detalhe;
  emit();
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

export function useWakeMode(): WakeMode {
  return useSyncExternalStore(subscribe, () => modo, () => 'off');
}

export function useWakeEngine(): WakeEngine {
  return useSyncExternalStore(subscribe, () => motor, () => 'chrome' as WakeEngine);
}

export function useWakeBlock(): string | null {
  return useSyncExternalStore(subscribe, () => bloqueio, () => null);
}

export function useWakeState(): { estado: WakeState; erro: string | null } {
  const e = useSyncExternalStore(subscribe, () => estado, () => 'parado' as WakeState);
  const d = useSyncExternalStore(subscribe, () => erro, () => null);
  return { estado: e, erro: d };
}
